import React from 'react';
import RouteHead from '../../components/RouteHead/RouteHead';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import './TermsOfServicePage.css';
import Footer from '../../components/Footer/Footer';

const TermsOfServicePage = () => {
  return (
    <div className="terms-container">
      <RouteHead path="/terms-of-service" />
      <Navbar />

      <div className="terms-content">
        <h1 className="terms-title">Terms of Service</h1>
        <p className="terms-last-updated">Last updated: {new Date().toLocaleDateString()}</p>

        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using VOIA Travel's website, mobile application, or services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            VOIA Travel is a social platform designed to connect travelers worldwide. Our platform enables users to discover travel inspiration, plan personalized trips, and share their travel experiences with a global community. Our services include but are not limited to:
          </p>
          <ul>
            <li><strong>Social Discovery:</strong> Get inspiration from other travelers by browsing shared trips, itineraries, and travel experiences</li>
            <li><strong>Personalized Trip Search:</strong> Search and discover personalized trip recommendations based on your preferences and interests</li>
            <li><strong>Trip Planning:</strong> Create and manage your own detailed itineraries with our planning tools</li>
            <li><strong>Trip Tracking:</strong> Track your trips and visits around the world, documenting your travel journey</li>
            <li><strong>Traveler Connections:</strong> Connect with other travelers, follow their journeys, and share your own experiences</li>
            <li><strong>Content Sharing:</strong> Share photos, reviews, tips, and travel stories with the community</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>3. User Accounts and Registration</h2>
          <p>To access certain features of our service, you may be required to:</p>
          <ul>
            <li>Create an account with accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Be responsible for all activities that occur under your account</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>4. Subscriptions and Payments</h2>
          <p>
            VOIA Travel is free to download and free to use. Core features (trip planning, trip tracking, your visited-countries map, and social features) do not require payment. We also offer an optional paid subscription, <strong>VOIA Premium</strong>.
          </p>

          <p><strong>What VOIA Premium includes:</strong></p>
          <ul>
            <li>Detailed travel statistics</li>
            <li>Adding places you want to visit from photos, saved to your wishlist</li>
            <li>Tagging <strong>@voia</strong> in trip chats to get assistance from a travel assistant with context on your itinerary</li>
          </ul>

          <p><strong>Pricing and billing period:</strong></p>
          <ul>
            <li>Monthly: €4.99 per month</li>
            <li>Yearly: €49.99 per year</li>
          </ul>
          <p>
            Prices are shown in euro. The amount actually charged is the price displayed in your regional App Store or Google Play storefront at the time of purchase, which may differ due to currency conversion and local taxes.
          </p>

          <p><strong>Purchases, auto-renewal and cancellation:</strong></p>
          <ul>
            <li>Subscriptions are purchased inside the VOIA mobile app and are billed to your Apple App Store or Google Play account at confirmation of purchase.</li>
            <li>Subscriptions renew automatically for the same period and at the same price unless cancelled at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours before the current period ends.</li>
            <li>You can manage or cancel your subscription at any time in your Apple App Store or Google Play account settings. Cancelling stops future renewals; it does not end the period you have already paid for, and access to Premium features continues until that period expires.</li>
            <li>Deleting the VOIA app does not cancel your subscription. You must cancel through the app store that processed the purchase.</li>
          </ul>

          <p><strong>Refunds:</strong> Purchases are processed by Apple and Google, so refunds are governed by their policies and must be requested through them. We cannot issue refunds for purchases we did not process.</p>

          <p><strong>Price changes:</strong> We may change subscription prices. Any change applies only to future billing periods, and we will give notice in advance as required by the applicable app store and by law. If you do not accept a new price, you can cancel before it takes effect.</p>

          <p><strong>Payment processing:</strong> We use RevenueCat to manage subscription entitlements. Payment details are handled by Apple and Google; we do not receive or store your card details.</p>

          <p><strong>Changes to Premium features:</strong> The features included in VOIA Premium may change over time as the service develops. We will not materially reduce what an active subscription provides during a period you have already paid for.</p>
        </section>

        <section className="terms-section">
          <h2>5. User Conduct and Community Guidelines</h2>
          <p>As a social platform, we expect all users to maintain respectful and appropriate behavior. You agree not to:</p>
          <ul>
            <li>Use the service for any unlawful purpose or in violation of any laws</li>
            <li>Post, share, or transmit any harmful, offensive, abusive, defamatory, discriminatory, or inappropriate content</li>
            <li>Harass, bully, threaten, or intimidate other users</li>
            <li>Share false, misleading, or fraudulent information about trips, destinations, or travel experiences</li>
            <li>Post content that violates the privacy or rights of others, including unauthorized photos or personal information</li>
            <li>Spam other users with unsolicited messages, advertisements, or promotional content</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
            <li>Interfere with or disrupt the service or servers connected to the service</li>
            <li>Use automated systems, bots, or scripts to access the service without permission</li>
            <li>Impersonate any person or entity, including other travelers, or misrepresent your affiliation</li>
            <li>Collect or harvest information about other users without their consent</li>
            <li>Engage in any activity that could harm, disable, or impair the platform or other users' experience</li>
          </ul>
          <p>
            We reserve the right to remove any content that violates these guidelines and to suspend or terminate accounts that engage in prohibited conduct.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Intellectual Property and User-Generated Content</h2>
          <p>
            <strong>Platform Content:</strong> All content, features, and functionality of the service, including but not limited to text, graphics, logos, images, and software, are the property of VOIA Travel or its licensors and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            <strong>Your Content:</strong> You retain ownership of any content you post, share, or upload to the platform, including photos, reviews, trip itineraries, and travel stories ("User Content"). However, by posting User Content on VOIA Travel, you grant us a worldwide, non-exclusive, royalty-free, perpetual, irrevocable license to use, reproduce, modify, adapt, publish, translate, distribute, and display such content on the platform and in our marketing materials.
          </p>
          <p>
            <strong>Content Responsibility:</strong> You are solely responsible for your User Content and represent that you have all necessary rights to post such content. You agree not to post content that infringes on the intellectual property rights of others.
          </p>
          <p>
            <strong>Other Users' Content:</strong> You may not reproduce, distribute, modify, or create derivative works from other users' content without their express written permission. You may share content within the platform using our sharing features.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Social Features and User Interactions</h2>
          <p>
            <strong>Connections and Following:</strong> Our platform allows you to connect with other travelers, follow their journeys, and be followed by others. You can control your privacy settings to manage who can see your content and connect with you.
          </p>
          <p>
            <strong>Content Moderation:</strong> We reserve the right to review, edit, remove, or refuse to post any User Content that violates these Terms of Service or our Community Guidelines. We are not obligated to monitor all content but may do so at our discretion.
          </p>
          <p>
            <strong>User Interactions:</strong> Interactions between users, including messages, comments, and connections, are the responsibility of the users involved. We are not responsible for disputes between users. If you encounter inappropriate behavior, please report it to us.
          </p>
          <p>
            <strong>Privacy of Shared Content:</strong> When you share content on the platform, it may be visible to other users based on your privacy settings. Be mindful of what you share and review your privacy settings regularly.
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Third-Party Services and Links</h2>
          <p>
            Our service may contain links to third-party websites, services, or resources. We are not responsible for the availability, accuracy, or content of these external sites. Your interactions with third-party services are solely between you and the third party.
          </p>
          <p>
            We may integrate with third-party travel providers (airlines, hotels, etc.) for booking services. We are not responsible for the terms, conditions, or policies of these third-party providers.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Disclaimer of Warranties</h2>
          <p>
            The service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that:
          </p>
          <ul>
            <li>The service will be uninterrupted, secure, or error-free</li>
            <li>The results obtained from using the service will be accurate or reliable</li>
            <li>Any errors in the service will be corrected</li>
            <li>The service will meet your specific requirements</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>10. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, VOIA Travel shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to your use of the service.
          </p>
          <p>
            <strong>User Content and Interactions:</strong> We are not responsible for the accuracy, reliability, or quality of User Content posted by other users. You use such content at your own risk. We are not liable for any interactions, disputes, or harm that may arise from your connections or communications with other users on the platform.
          </p>
          <p>
            <strong>Travel Information:</strong> Travel information, recommendations, and content shared by users are provided for informational purposes only. We do not guarantee the accuracy, safety, or suitability of destinations, accommodations, or travel suggestions shared on the platform.
          </p>
          <p>
            Our total liability for any claims arising from or related to the service shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Account Deletion and Content Removal</h2>
          <p>
            <strong>Account Deletion:</strong> You may delete your account at any time by contacting us or using the account deletion features in the service. Upon account deletion, your profile and personal information will be removed, but some content you've shared (such as public trip posts) may remain visible to other users unless you specifically request its removal.
          </p>
          <p>
            <strong>Content Removal:</strong> You can request removal of your User Content at any time. We will make reasonable efforts to remove your content, but some content may remain in cached or archived versions of the platform.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Modifications to Service</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the service.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the service immediately, without prior notice, for any reason, including if you breach these Terms of Service or our Community Guidelines. Upon termination, your right to use the service will cease immediately, and we may remove your User Content from the platform.
          </p>
          <p>
            You may terminate your account at any time by contacting us or using the account deletion features in the service.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms of Service at any time. We will notify users of any material changes by posting the new terms on this page and updating the "Last updated" date. Your continued use of the service after such changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>15. Governing Law</h2>
          <p>
            These Terms of Service shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions. Any disputes arising from these terms or your use of the service shall be resolved in the appropriate courts.
          </p>
        </section>

        <section className="terms-section">
          <h2>16. Severability</h2>
          <p>
            If any provision of these Terms of Service is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>
        </section>

        <section className="terms-section">
          <h2>17. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p className="terms-contact">
            Email: admin@voiatravel.com<br />
            VOIA Travel
          </p>
        </section>

        <div className="terms-footer-actions">
          <Link to="/" className="terms-back-link">← Back to Home</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;

